import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Rocket, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { PlanType, getPlanFeatures, formatCurrency } from "@/lib/billing-utils";

interface PricingCardProps {
  tier: PlanType;
  isCurrentPlan?: boolean;
  isHighlighted?: boolean;
  onSelectPlan?: () => void;
  monthlyCost?: number;
  features?: string[];
}

const tierConfig = {
  trial: {
    name: 'Trial',
    icon: Sparkles,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Perfect for getting started',
    price: 'Free',
    badge: '30 Days Free',
  },
  pro: {
    name: 'Pro',
    icon: Rocket,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Best for growing teams',
    price: 'Custom',
    badge: 'Most Popular',
  },
  enterprise: {
    name: 'Enterprise',
    icon: Crown,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    description: 'For large organizations',
    price: 'Custom',
    badge: 'Contact Sales',
  },
};

export function PricingCard({ 
  tier, 
  isCurrentPlan = false, 
  isHighlighted = false,
  onSelectPlan,
  monthlyCost,
  features: customFeatures,
}: PricingCardProps) {
  const config = tierConfig[tier];
  const Icon = config.icon;
  const features = customFeatures || getPlanFeatures(tier);

  return (
    <Card 
      className={cn(
        "relative flex flex-col transition-all duration-200",
        isHighlighted && "ring-2 ring-primary shadow-lg",
        isCurrentPlan && "border-primary"
      )}
    >
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary">Current Plan</Badge>
        </div>
      )}
      
      <CardHeader className="text-center pb-4">
        <div className={cn("w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center", config.bgColor)}>
          <Icon className={cn("w-6 h-6", config.color)} />
        </div>
        <CardTitle className="text-2xl">{config.name}</CardTitle>
        <CardDescription className="text-base mt-2">{config.description}</CardDescription>
        
        <div className="mt-4 space-y-2">
          <div className="text-4xl font-bold">
            {tier === 'trial' ? config.price : monthlyCost ? formatCurrency(monthlyCost) : 'Custom'}
          </div>
          {tier === 'pro' && monthlyCost && (
            <div className="text-sm text-muted-foreground">
              per month
            </div>
          )}
          {tier === 'trial' && (
            <div className="text-sm text-muted-foreground">
              for 30 days
            </div>
          )}
        </div>

        <Badge variant={tier === 'pro' ? 'default' : 'secondary'} className="mt-4">
          {config.badge}
        </Badge>
      </CardHeader>

      <CardContent className="flex-1">
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm text-muted-foreground">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter>
        {isCurrentPlan ? (
          <Button variant="outline" className="w-full" disabled>
            Current Plan
          </Button>
        ) : tier === 'enterprise' ? (
          <Button 
            variant={isHighlighted ? 'default' : 'outline'} 
            className="w-full"
            onClick={onSelectPlan}
          >
            Contact Sales
          </Button>
        ) : (
          <Button 
            variant={isHighlighted ? 'default' : 'outline'} 
            className="w-full"
            onClick={onSelectPlan}
          >
            {tier === 'trial' ? 'Continue Trial' : 'Upgrade'}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
